import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import {
  ArrowLeft,
  Database,
  Plus,
  Search,
  RefreshCcw,
  Edit2,
  Eye,
  EyeOff,
  Trash2,
  Star,
  SortAsc,
  SortDesc,
  GripVertical,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import Pagination from "@/components/ui/Pagination";
import {
  CmsCollectionService,
  CmsItemService,
  type CmsCollection,
  type CmsItem,
  type CmsItemStatus,
} from "@/services/cms";

function getCollectionKeyFromPath(location: string): string | null {
  const match = location.match(/^\/cms\/([^/]+)$/);
  return match ? match[1] : null;
}

const CmsItems: React.FC = () => {
  const [location, setLocation] = useLocation();
  const collectionKey = getCollectionKeyFromPath(location);

  const [collection, setCollection] = useState<CmsCollection | null>(null);
  const [items, setItems] = useState<CmsItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<CmsItemStatus | "">("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, string>>({});
  const [sortBy, setSortBy] = useState("order");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dragId, setDragId] = useState<string | null>(null);

  const limit = 20;

  const primaryField = useMemo(() => {
    if (!collection) return null;
    return collection.fields.find((f) => f.type === "text") || collection.fields[0] || null;
  }, [collection]);

  const tagFields = useMemo(() => {
    if (!collection) return [];
    return collection.fields.filter((f) => f.type === "select").slice(0, 2);
  }, [collection]);

  const activeFilterCount = Object.values(fieldFilters).filter(Boolean).length;
  const isDefaultOrderView = sortBy === "order" && !search && !statusFilter && activeFilterCount === 0;

  useEffect(() => {
    if (!collectionKey) return;
    loadCollectionThenItems();
  }, [collectionKey]);

  useEffect(() => {
    if (!collection) return;
    loadItems(collection.id);
  }, [page, search, statusFilter, fieldFilters, sortBy, sortOrder, collection?.id]);

  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const loadCollectionThenItems = async () => {
    if (!collectionKey) return;
    setIsLoading(true);
    setError("");
    try {
      const col = await CmsCollectionService.getByKey(collectionKey);
      setCollection(col);
      await loadItems(col.id);
    } catch (err: any) {
      console.error("Failed to load collection:", err);
      setError("Failed to load this collection. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadItems = async (collectionId: string) => {
    try {
      const filter = Object.fromEntries(Object.entries(fieldFilters).filter(([, v]) => v));
      const response = await CmsItemService.list(collectionId, {
        page,
        limit,
        search: search || undefined,
        status: statusFilter || undefined,
        filter: Object.keys(filter).length > 0 ? filter : undefined,
        sortBy,
        sortOrder,
      });
      setItems(response.items);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      console.error("Failed to load items:", err);
      setError("Failed to load items. Please try again.");
    }
  };

  const refresh = () => collection && loadItems(collection.id);

  const handleSort = (key: string) => {
    setSortBy((prevBy) => {
      if (prevBy === key) {
        setSortOrder((prevOrder) => (prevOrder === "ASC" ? "DESC" : "ASC"));
      } else {
        setSortOrder("ASC");
      }
      return key;
    });
    setPage(1);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.size === items.length ? new Set() : new Set(items.map((i) => i.id))));
  };

  const handleBulk = async (action: "delete" | "publish" | "unpublish") => {
    if (!collection || selectedIds.size === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${selectedIds.size} item(s)? This cannot be undone.`)) {
      return;
    }
    try {
      await CmsItemService.bulkAction(collection.id, Array.from(selectedIds), action);
      setSuccessMessage(`${selectedIds.size} item(s) updated.`);
      setSelectedIds(new Set());
      refresh();
    } catch (err: any) {
      setError(err?.message || "Bulk action failed.");
    }
  };

  const handleTogglePublish = async (item: CmsItem) => {
    try {
      await CmsItemService.setStatus(item.id, item.status === "published" ? "draft" : "published");
      refresh();
    } catch (err: any) {
      setError(err?.message || "Failed to update status.");
    }
  };

  const handleDelete = async (item: CmsItem) => {
    const label = primaryField ? String(item.fieldValues[primaryField.key] ?? item.slug ?? "this item") : item.slug;
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    try {
      await CmsItemService.remove(item.id);
      setSuccessMessage("Item deleted.");
      refresh();
    } catch (err: any) {
      setError(err?.message || "Failed to delete item.");
    }
  };

  /** Mobile/keyboard-friendly reorder, as an alternative to dragging. */
  const moveItem = async (index: number, direction: -1 | 1) => {
    if (!collection) return;
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const reordered = [...items];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setItems(reordered);
    try {
      // Offset by the current page's start so this doesn't renumber items on
      // page 2+ back down to 0..19, colliding with page 1's order values.
      const pageOffset = (page - 1) * limit;
      await CmsItemService.reorder(
        collection.id,
        reordered.map((it, i) => ({ id: it.id, order: pageOffset + i }))
      );
    } catch (err: any) {
      setError("Failed to save the new order.");
      refresh();
    }
  };

  const handleDrop = async (targetIndex: number) => {
    if (!collection || !dragId) return;
    const sourceIndex = items.findIndex((i) => i.id === dragId);
    setDragId(null);
    if (sourceIndex === -1 || sourceIndex === targetIndex) return;

    const reordered = [...items];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setItems(reordered);
    try {
      const pageOffset = (page - 1) * limit;
      await CmsItemService.reorder(
        collection.id,
        reordered.map((it, i) => ({ id: it.id, order: pageOffset + i }))
      );
    } catch (err: any) {
      setError("Failed to save the new order.");
      refresh();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-sm text-red-600">{error || "Collection not found."}</p>
      </div>
    );
  }

  const sortIcon = (key: string) =>
    sortBy === key ? (sortOrder === "ASC" ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setLocation("/cms")}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <Database className="h-8 w-8 text-primary shrink-0" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{collection.name}</h1>
              <p className="text-gray-600">{collection.description || `Items in the "${collection.name}" collection`}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refresh}
            className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <RefreshCcw className="h-4 w-4" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={() => setLocation(`/cms/${collection.key}/items/new`)}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>New Item</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as CmsItemStatus | "");
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm"
        >
          <option value="">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
        {tagFields.map((field) => (
          <select
            key={field.key}
            value={fieldFilters[field.key] || ""}
            onChange={(e) => {
              setFieldFilters((prev) => ({ ...prev, [field.key]: e.target.value }));
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary text-sm"
          >
            <option value="">All {field.label}</option>
            {(field.options || []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ))}
      </div>

      {!isDefaultOrderView && (
        <p className="text-xs text-gray-500">
          Drag-to-reorder is only available in the default, unsorted/unfiltered view. Clear search, filters, and
          sorting to reorder.
        </p>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-primary/5 border border-primary/20 rounded-md px-4 py-2.5">
          <span className="text-sm font-medium text-gray-700">{selectedIds.size} selected</span>
          <button
            onClick={() => handleBulk("publish")}
            className="text-sm text-green-700 hover:text-green-800 font-medium"
          >
            Publish
          </button>
          <button
            onClick={() => handleBulk("unpublish")}
            className="text-sm text-orange-700 hover:text-orange-800 font-medium"
          >
            Unpublish
          </button>
          <button onClick={() => handleBulk("delete")} className="text-sm text-red-600 hover:text-red-700 font-medium">
            Delete
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-sm text-gray-500 hover:text-gray-700 ml-auto"
          >
            Clear
          </button>
        </div>
      )}

      {/* Items */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {items.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <Database className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-lg font-medium">No items found</p>
            <p className="text-sm">Create the first item in this collection to get started.</p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <ul className="divide-y divide-gray-200 md:hidden">
              {items.map((item, index) => (
                <li key={item.id} className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <label className="flex items-start gap-2 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        onChange={() => toggleSelect(item.id)}
                        className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded shrink-0"
                      />
                      <span className="text-sm font-medium text-gray-900 flex items-center gap-1.5 min-w-0">
                        {item.isFeatured && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />}
                        <span className="truncate">
                          {primaryField ? String(item.fieldValues[primaryField.key] ?? "") : item.slug}
                        </span>
                      </span>
                    </label>
                    <span
                      className={cn(
                        "shrink-0 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full",
                        item.status === "published" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                      )}
                    >
                      {item.status === "published" ? "Published" : "Draft"}
                    </span>
                  </div>
                  {tagFields.length > 0 && (
                    <p className="text-xs text-gray-500 truncate pl-6">
                      {tagFields.map((f) => item.fieldValues[f.key]).filter(Boolean).join(" · ")}
                    </p>
                  )}
                  <div className="flex items-center gap-3 pt-1 pl-6">
                    <button
                      onClick={() => setLocation(`/cms/${collection.key}/items/${item.id}/edit`)}
                      className="text-primary hover:text-primary/80"
                      aria-label="Edit item"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleTogglePublish(item)}
                      className={item.status === "published" ? "text-orange-600" : "text-green-600"}
                      aria-label={item.status === "published" ? "Unpublish" : "Publish"}
                    >
                      {item.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button onClick={() => handleDelete(item)} className="text-red-600" aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    {isDefaultOrderView && (
                      <span className="flex items-center gap-1 ml-auto">
                        <button
                          onClick={() => moveItem(index, -1)}
                          disabled={index === 0}
                          className="text-gray-400 disabled:opacity-30"
                          aria-label="Move up"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => moveItem(index, 1)}
                          disabled={index === items.length - 1}
                          className="text-gray-400 disabled:opacity-30"
                          aria-label="Move down"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {isDefaultOrderView && <th className="w-8 px-2 py-3" />}
                    <th className="w-8 px-2 py-3">
                      <input
                        type="checkbox"
                        checked={items.length > 0 && selectedIds.size === items.length}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => primaryField && handleSort(primaryField.key)}
                    >
                      <div className="flex items-center gap-1">
                        <span>{primaryField?.label || "Item"}</span>
                        {primaryField && sortIcon(primaryField.key)}
                      </div>
                    </th>
                    {tagFields.map((field) => (
                      <th key={field.key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {field.label}
                      </th>
                    ))}
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center gap-1">
                        <span>Status</span>
                        {sortIcon("status")}
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort("updatedAt")}
                    >
                      <div className="flex items-center gap-1">
                        <span>Updated</span>
                        {sortIcon("updatedAt")}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item, index) => (
                    <tr
                      key={item.id}
                      className={cn("hover:bg-gray-50", dragId === item.id && "opacity-40")}
                      draggable={isDefaultOrderView}
                      onDragStart={() => setDragId(item.id)}
                      onDragOver={(e) => isDefaultOrderView && e.preventDefault()}
                      onDrop={() => isDefaultOrderView && handleDrop(index)}
                      onDragEnd={() => setDragId(null)}
                    >
                      {isDefaultOrderView && (
                        <td className="px-2 py-3 text-gray-300 cursor-grab active:cursor-grabbing">
                          <GripVertical className="h-4 w-4" />
                        </td>
                      )}
                      <td className="px-2 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={() => toggleSelect(item.id)}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5 max-w-sm">
                          {item.isFeatured && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />}
                          <span className="truncate">
                            {primaryField ? String(item.fieldValues[primaryField.key] ?? "") : item.slug}
                          </span>
                        </div>
                        {item.slug && <div className="text-xs text-gray-400 truncate max-w-sm">/{item.slug}</div>}
                      </td>
                      {tagFields.map((field) => (
                        <td key={field.key} className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          {String(item.fieldValues[field.key] ?? "—")}
                        </td>
                      ))}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex px-2 py-1 text-xs font-semibold rounded-full",
                            item.status === "published" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                          )}
                        >
                          {item.status === "published" ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        {new Date(item.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setLocation(`/cms/${collection.key}/items/${item.id}/edit`)}
                            className="text-primary hover:text-primary/80 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleTogglePublish(item)}
                            className={cn(
                              "transition-colors",
                              item.status === "published" ? "text-orange-600 hover:text-orange-700" : "text-green-600 hover:text-green-700"
                            )}
                            title={item.status === "published" ? "Unpublish" : "Publish"}
                          >
                            {item.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className="text-red-600 hover:text-red-700 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          {isDefaultOrderView && (
                            <span className="flex items-center gap-0.5">
                              <button
                                onClick={() => moveItem(index, -1)}
                                disabled={index === 0}
                                className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                                aria-label="Move up"
                              >
                                <ChevronUp className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => moveItem(index, 1)}
                                disabled={index === items.length - 1}
                                className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                                aria-label="Move down"
                              >
                                <ChevronDown className="h-4 w-4" />
                              </button>
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} total={total} pageSize={limit} itemLabel="items" />
        </div>
      </div>
    </div>
  );
};

export default CmsItems;
